import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { generateSEOMeta } from '@/components/feature/SEOMeta';
import { getOrganizationSchema, getWebPageSchema, getBreadcrumbSchema, getFAQSchema } from '@/components/feature/JsonLD';
import { testimonials, faqs } from '@/mocks/comics';

const seo = generateSEOMeta('about');

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    document.title = seo.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', seo.description);
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', seo.keywords);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus(null);
    setFormError(null);

    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);

    try {
      const res = await fetch('https://readdy.ai/api/form/d9pbo6iftmtnfcfh0k1g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getOrganizationSchema()) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(getWebPageSchema(seo.title, seo.description, 'https://www.hanmanzhijia.com/about')),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getBreadcrumbSchema([
              { name: '首页', url: 'https://www.hanmanzhijia.com/' },
              { name: '关于我们' },
            ])
          ),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getFAQSchema(faqs)) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          {/* Brand Introduction */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="flex-1">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                  关于 <span className="text-primary-600">韩漫之家</span>
                </h1>
                <p className="mt-6 text-base text-foreground-600 leading-relaxed">
                  韩漫之家创立于2024年，是专注于韩国漫画领域的专业在线免费阅读平台。
                  我们致力于为全球华语读者提供最全面、最高清的韩漫阅读体验。
                </p>
                <p className="mt-4 text-base text-foreground-600 leading-relaxed">
                  平台汇集了超过2000部优质韩国漫画作品，涵盖恋爱、奇幻、动作、悬疑、搞笑、
                  剧情、恐怖、校园等多种类型。我们采用高清下拉式阅读模式，
                  让读者可以流畅地享受每一部韩漫作品的精美画风和动人故事。
                </p>
                <p className="mt-4 text-base text-foreground-600 leading-relaxed">
                  韩漫之家始终坚持"免费、高清、全面"的服务理念，与韩国漫画产业保持紧密合作，
                  力求实现最新漫画的同步更新。无论是最新连载还是经典完结作品，
                  您都能在这里找到心仪的韩漫。我们的目标是成为全球华语读者首选的韩漫阅读平台。
                </p>
              </div>
              <div className="lg:w-[400px] flex-shrink-0">
                <div className="aspect-[4/3] rounded-xl overflow-hidden bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20illustration%20of%20a%20cozy%20reading%20space%20with%20a%20large%20tablet%20displaying%20colorful%20Korean%20manhwa%20pages%2C%20surrounded%20by%20books%20and%20a%20warm%20lamp%2C%20Korean%20webtoon%20art%20style%2C%20soft%20warm%20lighting%2C%20comfortable%20atmosphere%2C%20creative%20composition&width=800&height=600&seq=about-banner&orientation=landscape"
                    alt="韩漫之家阅读体验"
                    title="韩漫之家 - 免费韩漫在线阅读平台"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="bg-background-100 py-12 md:py-14">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { num: '2000+', label: '韩漫作品' },
                  { num: '500万+', label: '活跃读者' },
                  { num: '8', label: '漫画分类' },
                  { num: '99.9%', label: '服务可用率' },
                ].map((stat, i) => (
                  <div key={i} className="text-center p-6 rounded-xl bg-background-50 border border-background-200/60">
                    <p className="font-heading text-3xl font-bold text-primary-600">{stat.num}</p>
                    <p className="mt-2 text-sm text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Mission & Values */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-12 md:py-16">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center">
              我们的使命
            </h2>
            <p className="mt-3 text-sm text-foreground-500 text-center max-w-xl mx-auto">
              让每一位韩漫爱好者都能免费享受高品质阅读
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: '品质保证',
                  desc: '每一部作品都经过精心筛选，确保提供高清画质和完整内容，让您享受极致的阅读体验。',
                },
                {
                  icon: 'ri-flashlight-line',
                  title: '极速更新',
                  desc: '与韩国同步更新，热门连载作品24-48小时内上线，让追更不再煎熬。',
                },
                {
                  icon: 'ri-user-heart-line',
                  title: '用户至上',
                  desc: '以读者体验为核心，持续优化阅读功能和界面设计，打造最舒适便捷的韩漫阅读平台。',
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="p-6 rounded-xl bg-background-100 border border-background-200/60 text-center"
                >
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-50">
                    <i className={`${item.icon} text-2xl text-primary-600`}></i>
                  </div>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-800">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-500 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section className="bg-background-100 py-12 md:py-16">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="max-w-2xl mx-auto">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center">
                  联系我们
                </h2>
                <p className="mt-3 text-sm text-foreground-500 text-center">
                  有任何问题或建议？欢迎给我们留言
                </p>

                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="mt-8 space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-all duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-all duration-200"
                        placeholder="请输入您的电子邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-all duration-200"
                      placeholder="请输入留言主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-all duration-200 resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    />
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    className="contact-form-alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {formStatus === 'success' && (
                    <div className="p-4 rounded-lg bg-accent-50 border border-accent-200 text-sm text-accent-800">
                      <i className="ri-checkbox-circle-line mr-2"></i>
                      感谢您的留言！我们会尽快回复您。
                    </div>
                  )}
                  {formStatus === 'error' && formError && (
                    <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                      <i className="ri-error-warning-line mr-2"></i>
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full transition-all duration-200 text-sm whitespace-nowrap cursor-pointer"
                  >
                    发送留言
                  </button>
                </form>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-12 md:py-16">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center">
              常见问题
            </h2>
            <p className="mt-3 text-sm text-foreground-500 text-center max-w-xl mx-auto">
              关于韩漫之家的常见疑问解答
            </p>
            <div className="mt-10 max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, i) => (
                <details
                  key={i}
                  className="group rounded-xl bg-background-50 border border-background-200/60 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-100 transition-colors duration-200">
                    <span className="text-sm font-medium text-foreground-800 pr-4">
                      {faq.question}
                    </span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}